<script lang="ts">
  // Fenêtre de choix du serveur : recherche, latence mesurée en direct, choix automatique.
  import { probeServer } from '@mesurio/engine';
  import type { PublicServer } from '../lib/api';

  let { servers, selected = $bindable('auto') }: { servers: PublicServer[]; selected?: string } = $props();

  let dlg: HTMLDialogElement;
  let q = $state('');
  let rtt = $state<Record<string, number | null>>({});

  // Ouverture directement dans le gestionnaire de clic (geste utilisateur) : plus fiable, notamment sous Safari.
  export function show() {
    if (!dlg.open) dlg.showModal();
    void measure();
  }
  // Clic sur le fond assombri (hors du cadre de la fenêtre) : fermeture.
  function backdrop(e: MouseEvent) {
    if (e.target !== dlg) return;
    const r = dlg.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) hide();
  }
  function hide() {
    if (dlg.open) dlg.close();
  }

  async function measure() {
    for (const s of servers.slice(0, 12)) {
      if (rtt[s.id] !== undefined) continue;
      try {
        rtt[s.id] = await probeServer(s.url, AbortSignal.timeout(3000));
      } catch {
        rtt[s.id] = null;
      }
    }
  }

  const norm = (x: string) => x.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  const list = $derived(servers.filter((s) => !q || norm(`${s.city} ${s.sponsor} ${s.name} ${s.departement} ${s.pays_nom}`).includes(norm(q))));
  function choose(id: string) {
    selected = id;
    hide();
  }
</script>

<dialog bind:this={dlg} onclick={backdrop} aria-labelledby="titre-serveurs">
  <div class="head">
    <h2 id="titre-serveurs">Choisir un serveur</h2>
    <button class="close" onclick={hide} aria-label="Fermer">
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
    </button>
  </div>
  <label class="search">
    <span class="sr-only">Rechercher un serveur</span>
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" stroke-width="2" /><path d="m16 16 4.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
    <input type="search" placeholder="Ville, hébergeur, opérateur…" bind:value={q} />
  </label>
  <ul role="listbox" aria-label="Serveurs disponibles">
    <li role="option" aria-selected={selected === 'auto'}>
      <button onclick={() => choose('auto')}>
        <span class="main"><strong>Automatique</strong><span class="sub">Le serveur le plus réactif parmi les plus proches</span></span>
        {#if selected === 'auto'}<span class="check" aria-hidden="true">✓</span>{/if}
      </button>
    </li>
    {#each list as s (s.id)}
      <li role="option" aria-selected={selected === s.id}>
        <button onclick={() => choose(s.id)}>
          <span class="main">
            <strong>{s.sponsor}</strong>
            <span class="sub">{s.city} ({s.pays === 'FR' ? s.departement : s.pays_nom}) · {s.distance_km.toLocaleString('fr-FR')} km · {s.capacity_mbps >= 1000 ? s.capacity_mbps / 1000 + ' Gbit/s' : s.capacity_mbps + ' Mbit/s'}</span>
          </span>
          <span class="rtt">
            {#if rtt[s.id] === undefined}<span class="muted">…</span>
            {:else if rtt[s.id] === null}<span class="muted">injoignable</span>
            {:else}{Math.round(rtt[s.id]!)} ms{/if}
          </span>
          {#if selected === s.id}<span class="check" aria-hidden="true">✓</span>{/if}
        </button>
      </li>
    {:else}
      <li class="empty muted">Aucun serveur ne correspond à « {q} ».</li>
    {/each}
  </ul>
</dialog>

<style>
  dialog { width: min(560px, calc(100vw - 24px)); max-height: min(78vh, 720px); padding: 0; border: 1px solid var(--border); border-radius: 26px; background: color-mix(in oklab, var(--surface-solid) 88%, transparent); backdrop-filter: blur(24px) saturate(160%); color: var(--text); box-shadow: 0 30px 80px -20px rgb(0 0 0 / 55%), var(--glow); }
  dialog::backdrop { background: rgb(5 5 14 / 55%); backdrop-filter: blur(6px); }
  .head { display: flex; align-items: center; justify-content: space-between; padding: 18px 18px 6px 22px; }
  h2 { margin: 0; font-size: 1.25rem; }
  .close { border: 0; background: transparent; padding: 0 8px; color: var(--muted); }
  .search { display: flex; align-items: center; gap: 0.5rem; margin: 6px 18px 10px; padding: 0 12px; background: var(--surface-2); border-radius: 12px; color: var(--muted); font-weight: 400; }
  .search input { flex: 1; border: 0; background: transparent; min-width: 0; padding-left: 0; }
  .search input:focus { outline: none; }
  .search:focus-within { outline: 3px solid var(--focus); outline-offset: 1px; }
  ul { list-style: none; margin: 0; padding: 0 8px 12px; overflow-y: auto; max-height: calc(min(78vh, 720px) - 130px); }
  li button { width: 100%; display: flex; align-items: center; gap: 12px; border: 0; background: transparent; padding: 12px 14px; border-radius: 12px; text-align: left; min-height: 60px; }
  li button:hover { background: var(--surface-2); }
  li[aria-selected='true'] button { background: var(--surface-2); }
  .main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
  .sub { color: var(--muted); font-size: 0.9rem; }
  .rtt { font-variant-numeric: tabular-nums; color: var(--text); font-size: 0.95rem; white-space: nowrap; }
  .check { color: var(--primary); font-weight: 800; }
  .empty { padding: 16px; }
</style>
